import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { impersonateUser } from '@adornis/users/db/a-user.js';
import { html } from '@lit-labs/preact-signals';
import { nothing } from 'lit';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { Person } from '../../entities/person.js';
import { UserActions } from '../../UserPolicy.js';

export const UserListInlineControls = webcomponent(
  'user-list-inline-control',
  (props: {
    user: NarrowedEntity<Person, { email: 1; _id: 1; profilDaten: { stammdaten: { vorname: 1; nachname: 1 } } }>;
  }) => {
    const user = useObservable(() => currentUser(), []);

    const { can: canImpersonate } = useMartinusABAC({
      action: UserActions.IMPERSONATE,
    });

    const { can: canRemoveUser } = useMartinusABAC({
      action: UserActions.REMOVE_USER,
    });

    if ((!canImpersonate && !canRemoveUser) || props.user._id === user?._id) return nothing;

    return html`
      <div class="martinus-action-toolbar">
        ${canImpersonate
          ? html`
              <button
                data-tip="Impersonieren"
                ${hoverTooltip()}
                class="btn btn-sm btn-ghost join-item"
                @click=${async (e: Event) => {
                  e.stopPropagation();
                  e.preventDefault();

                  await guardDialog(
                    async () => {
                      impersonateUser(props.user._id, Person._class)();
                    },
                    {
                      success: () => {
                        void SgSnackbar.show('Benutzer wurde impersoniert');
                        goTo('/');
                        location.reload();
                      },
                    },
                  );
                }}
              >
                <span class="icon-[material-symbols--background-replace] text-lg"></span>
              </button>
            `
          : nothing}
        ${canRemoveUser
          ? html`
              <button
                data-tip="Buchung beenden"
                ${hoverTooltip()}
                class="btn btn-sm  btn-error join-item"
                @click=${async (e: Event) => {
                  e.stopPropagation();
                  e.preventDefault();

                  if (
                    !(await confirmDialog(
                      `Sind Sie sicher, dass sie den Nutzer ${
                        props.user.profilDaten?.stammdaten?.vorname || props.user.email
                      } ${props.user.profilDaten?.stammdaten?.nachname} löschen möchten?`,
                      { highlightDecline: true, acceptText: 'Nutzer löschen', declineText: 'Abbrechen' },
                    ))
                  )
                    return;

                  await guardDialog(
                    async () => {
                      await props.user.remove();
                    },
                    {
                      successText: 'Benutzer wurde gelöscht',
                    },
                  );
                }}
              >
                <span class="icon-[material-symbols--delete] text-lg"></span>
              </button>
            `
          : nothing}
      </div>
    `;
  },
  {
    shadowRoot: true,
  },
);
